import { useState, useRef, useEffect } from 'react';
import type { HuddlePost } from '@lib/api';
import { huddleApi, resolveMediaUrl } from '@lib/api';
import { MarkdownContent } from '../MarkdownContent';
import { HuddleComments } from '../HuddleComments';
import { HuddleComposer } from '../HuddleComposer';
import { toPostAttachment } from '../api';
import type { ComposerContent, MediaItem } from '../types';
import { useRouter } from '../../../ui/router';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

// ── URL Resolution ────────────────────────────────────────────────────────────
// Posts persist attachment URLs by path; `resolveMediaUrl` binds them to
// whichever backend origin is serving this session (and repairs older posts
// that stored an absolute URL against a host the backend has since left).
const resolveAttachmentUrl = resolveMediaUrl;

// ── Avatar ────────────────────────────────────────────────────────────────────
type AvatarColor = 'indigo' | 'teal' | 'coral' | 'amber' | 'pink' | 'green';

const avatarClasses: Record<AvatarColor, string> = {
  indigo: 'bg-indigo-100 text-indigo-600',
  teal: 'bg-teal-100 text-teal-600',
  coral: 'bg-red-100 text-red-500',
  amber: 'bg-amber-100 text-amber-600',
  pink: 'bg-pink-100 text-pink-500',
  green: 'bg-green-100 text-green-600',
};

function Avatar({
  initials,
  color,
  size = 'md',
}: {
  initials: string;
  color: AvatarColor;
  size?: 'sm' | 'md';
}) {
  const sz = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-9 h-9 text-[13px]';
  return (
    <div
      className={`${sz} rounded-full flex items-center justify-center font-semibold shrink-0 ${avatarClasses[color]}`}
    >
      {initials}
    </div>
  );
}

function getUserColor(userId: string): AvatarColor {
  const colors: AvatarColor[] = ['indigo', 'teal', 'coral', 'amber', 'pink', 'green'];
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

function getUserInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

// ── PostCard ──────────────────────────────────────────────────────────────────
interface PostCardProps {
  post: HuddlePost;
  currentUserId: string;
  canEdit: boolean;
  canDelete: boolean;
  onPostUpdated?: () => void;
  /** Briefly highlighted when navigated to directly (e.g. from the dashboard's
   *  Recent Activity feed via `/app/huddle?postId=`). */
  highlighted?: boolean;
}

export function PostCard({
  post,
  currentUserId,
  canEdit,
  canDelete,
  onPostUpdated,
  highlighted,
}: PostCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes?.length ?? 0);
  const [hasLiked, setHasLiked] = useState(post.likes?.includes(currentUserId) ?? false);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Reset edit state and update counts when post changes
  useEffect(() => {
    setLikeCount(post.likes?.length ?? 0);
    setHasLiked(post.likes?.includes(currentUserId) ?? false);
    setCommentCount(post.commentCount ?? 0);
  }, [post, currentUserId]);

  const handleEdit = () => {
    setIsEditing(true);
    setShowMenu(false);
  };

  const handleEditPost = async (content: ComposerContent) => {
    try {
      await huddleApi.updatePost(
        post.id,
        { text: content.text, mentions: content.mentions.map((m) => m.userId) },
        {
          attachments: content.attachments.map(toPostAttachment),
          ticketId: content.ticketId ?? null,
        },
      );
      setIsEditing(false);
      onPostUpdated?.();
    } catch (error) {
      console.error('[PostCard] Failed to update post:', error);
      alert('Failed to update post');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    setIsDeleting(true);
    try {
      await huddleApi.deletePost(post.id);
      onPostUpdated?.();
    } catch (error) {
      console.error('[PostCard] Failed to delete post:', error);
      alert('Failed to delete post');
      setIsDeleting(false);
    }
  };

  const handleTicketClick = () => {
    if (post.ticketId) window.location.href = `/app/tickets/${post.ticketId}`;
  };

  const formatTimestamp = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (isDeleting) return null;

  const authorName = (post as any).userName || 'Unknown User';
  const authorInitials = (post as any).userInitials || getUserInitials(authorName);
  const avatarColor = getUserColor(post.userId);
  const { navigate } = useRouter();
  const goToAuthorProfile = () => navigate(`/app/profile/${post.userId}`);

  return (
    <div
      id={`huddle-post-${post.id}`}
      data-testid="post-card"
      className={`border-b border-gray-100 dark:border-neutral-700 px-5 pt-4 bg-white dark:bg-neutral-800 md:mb-4 md:rounded-xl md:border md:border-gray-200 dark:md:border-neutral-700 transition-shadow ${
        highlighted ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-neutral-900' : ''
      }`}
    >
      {/* ── Author header ── */}
      <div className="flex items-center gap-2.5 mb-3">
        <button
          type="button"
          onClick={goToAuthorProfile}
          aria-label={`View ${authorName}'s profile`}
          className="shrink-0 rounded-full transition-opacity hover:opacity-80"
        >
          <Avatar initials={authorInitials} color={avatarColor} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToAuthorProfile}
              className="font-medium text-sm text-gray-900 dark:text-white hover:underline"
            >
              {authorName}
            </button>
            <span className="text-xs text-gray-500 dark:text-neutral-400">
              {formatTimestamp(post.createdAt)}
            </span>
            {post.updatedAt && post.updatedAt !== post.createdAt && (
              <span className="text-xs text-gray-400 dark:text-neutral-500 italic">edited</span>
            )}
          </div>
        </div>

        {/* Three-dot menu */}
        {(canEdit || canDelete) && (
          <div className="relative" ref={menuRef}>
            <button
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
              onClick={() => setShowMenu(!showMenu)}
            >
              <svg
                className="w-4 h-4 text-gray-400 dark:text-neutral-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-gray-200 dark:border-neutral-700 z-10">
                {canEdit && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-t-lg"
                    onClick={handleEdit}
                  >
                    Edit post
                  </button>
                )}
                {canDelete && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-b-lg"
                    onClick={handleDelete}
                  >
                    Delete post
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Ticket badge ── */}
      {post.ticketId && (
        <div
          // max-w-full keeps a long ticket title from widening the chip past the
          // card and scrolling the whole feed sideways; the title's `truncate`
          // only engages once the chip is bounded.
          className="inline-flex max-w-full items-center gap-1.5 px-2.5 py-1 mb-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
          onClick={handleTicketClick}
        >
          <div className="w-3.5 h-3.5 rounded bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0">
            <svg
              className="w-2 h-2 text-amber-500 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
              />
            </svg>
          </div>
          {/* min-w-0: a flex item defaults to min-width:auto and would otherwise
              refuse to shrink below its text, defeating `truncate`. */}
          <span className="min-w-0 truncate text-xs font-medium text-amber-700 dark:text-amber-300">
            {post.ticketTitle || 'Linked Ticket'}
          </span>
        </div>
      )}

      {/* ── Post content ── */}
      {isEditing ? (
        <div className="mb-3 -mx-5 border-y border-gray-100 dark:border-neutral-700">
          <HuddleComposer
            editing
            submitLabel="Update post"
            userInitials={authorInitials}
            userColor={avatarColor}
            initialText={post.content.text}
            initialTicketId={post.ticketId}
            initialAttachments={(post.attachments ?? []).map<MediaItem>((a) => ({
              id: a.mediaId,
              url: a.url,
              filename: a.filename ?? '',
              type: a.type === 'file' ? 'document' : a.type,
              size: 0,
            }))}
            collabRoom={post.id}
            onPost={handleEditPost}
            onCancel={handleCancelEdit}
          />
        </div>
      ) : // ── Render markdown instead of plain text ──
      post.content.text?.trim() ? (
        <div className="mb-3">
          <MarkdownContent content={post.content.text} />
        </div>
      ) : null}

      {/* ── Attachments ── */}
      {!isEditing && post.attachments && post.attachments.length > 0 && (
        <div className={`mb-3 ${post.attachments.length === 1 ? '' : 'grid grid-cols-2'} gap-2`}>
          {post.attachments.map((attachment) => (
            <div key={attachment.mediaId} className="relative rounded-xl overflow-hidden">
              {attachment.type === 'image' && (
                <div className="relative w-full bg-gray-100 dark:bg-neutral-800 rounded-xl max-h-[500px] flex items-center justify-center">
                  <img
                    src={resolveAttachmentUrl(attachment.url)}
                    alt={attachment.filename || 'Image'}
                    className="max-w-full max-h-[500px] object-contain rounded-xl"
                  />
                </div>
              )}
              {attachment.type === 'video' && (
                <div className="relative w-full rounded-xl overflow-hidden bg-black">
                  <video
                    controls
                    className="w-full rounded-xl max-h-96"
                    src={resolveAttachmentUrl(attachment.url)}
                    poster={
                      attachment.thumbnailUrl
                        ? resolveAttachmentUrl(attachment.thumbnailUrl)
                        : undefined
                    }
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              {attachment.type === 'file' && (
                <a
                  href={resolveAttachmentUrl(attachment.url)}
                  download={attachment.filename}
                  className="block bg-neutral-100 dark:bg-neutral-700 p-4 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-8 h-8 text-gray-500 dark:text-neutral-400 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {attachment.filename || 'Document'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-neutral-400">
                        Click to download
                      </p>
                    </div>
                  </div>
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex items-center gap-0.5 py-2 border-t border-gray-100 dark:border-neutral-700 -mx-5 px-5">
        <button
          onClick={async () => {
            try {
              // Optimistic update
              const previousLiked = hasLiked;
              const previousCount = likeCount;
              setHasLiked(!previousLiked);
              setLikeCount(previousLiked ? previousCount - 1 : previousCount + 1);

              // Call API (WebSocket will broadcast update to other clients)
              await huddleApi.toggleLike(post.id);
              // Don't set count from response - let WebSocket update handle it
            } catch (error) {
              console.error('[PostCard] Failed to toggle like:', error);
              // Revert optimistic update on error
              setHasLiked(hasLiked);
              setLikeCount(likeCount);
            }
          }}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg transition-colors ${
            hasLiked
              ? 'text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300'
              : 'text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-400'
          }`}
        >
          <svg
            className="w-4 h-4"
            fill={hasLiked ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          {likeCount}
        </button>
        <div className="w-px h-4 bg-gray-200 dark:bg-neutral-700 mx-1" />
        <button
          onClick={() => setShowComments(!showComments)}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg transition-colors ${
            showComments
              ? 'text-indigo-500 dark:text-indigo-400'
              : 'text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-400'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          {commentCount}
        </button>
        <div className="w-px h-4 bg-gray-200 dark:bg-neutral-700 mx-1" />
        <button
          onClick={async () => {
            const postUrl = `${window.location.origin}/app/huddle?postId=${post.id}`;
            try {
              // Use Capacitor Share plugin on native platforms
              if (Capacitor.isNativePlatform()) {
                await Share.share({
                  title: `${authorName}'s post`,
                  text:
                    post.content.text.length > 200
                      ? post.content.text.substring(0, 197) + '...'
                      : post.content.text,
                  url: postUrl,
                  dialogTitle: 'Share post',
                });
              } else if (navigator.share) {
                // Use Web Share API on web
                await navigator.share({
                  title: `${authorName}'s post`,
                  text:
                    post.content.text.length > 200
                      ? post.content.text.substring(0, 197) + '...'
                      : post.content.text,
                  url: postUrl,
                });
              } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(postUrl);
                alert('Link copied to clipboard!');
              }
            } catch (error) {
              if ((error as Error).name !== 'AbortError') {
                console.error('[PostCard] Failed to share:', error);
              }
            }
          }}
          className="flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-lg transition-colors text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-400"
          title="Share post"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          Share
        </button>
      </div>

      {/* ── Comments section ── */}
      {showComments && (
        <HuddleComments
          postId={post.id}
          currentUserId={currentUserId}
          canDelete={canDelete}
          onCommentAdded={() => {
            setCommentCount(commentCount + 1);
            onPostUpdated?.();
          }}
          onCommentDeleted={() => {
            setCommentCount(commentCount - 1);
            onPostUpdated?.();
          }}
        />
      )}
    </div>
  );
}
